import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import ScrollToTopLink from "./ScrollToTopLink"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useLanguage } from "../context/LanguageContext"
import { billingEnabled } from "../config/features"
import { useSubscription } from "../context/SubscriptionContext"

export default function NavBar() {
  const { user, logout } = useAuth()
  const { toggleTheme, isDark } = useTheme()
  const { language, changeLanguage, t, availableLanguages } = useLanguage()
  const { isPro } = useSubscription()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const langRef = useRef(null)

  const navLangLabels = { en: "EN", my: "MM", zh: "中" }
  const currentLang = availableLanguages.find((lang) => lang.code === language)

  useEffect(() => {
    if (!langOpen) return
    const onClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false)
      }
    }
    const onEscape = (e) => {
      if (e.key === "Escape") setLangOpen(false)
    }
    document.addEventListener("click", onClickOutside)
    document.addEventListener("keydown", onEscape)
    return () => {
      document.removeEventListener("click", onClickOutside)
      document.removeEventListener("keydown", onEscape)
    }
  }, [langOpen])

  const selectLanguage = (code) => {
    changeLanguage(code)
    setLangOpen(false)
  }

  const adminLabel = t("adminPanel") || "Admin"

  const authenticatedNavItems = [
    { name: t("dashboard"), path: "/dashboard" },
    { name: t("reading"), path: "/reading" },
    { name: t("writing"), path: "/writing" },
    { name: t("listening"), path: "/listening" },
    { name: t("speaking"), path: "/speaking" },
    { name: t("vocabulary"), path: "/vocabulary" },
    { name: t("grammar"), path: "/grammar" },
    { name: t("activities"), path: "/activities" },
    { name: t("profile"), path: "/profile" },
    ...(user?.is_admin
      ? [
          { name: adminLabel, path: "/admin/users" },
          { name: t("manageFeedback"), path: "/admin/feedback" },
        ]
      : []),
  ]

  const navItems = user ? authenticatedNavItems : []

  const languageToggle = (
    <div className="nav-lang-dropdown relative z-30" ref={langRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setLangOpen((open) => !open)
        }}
        className="nav-lang-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={langOpen}
        aria-label={t("language")}
      >
        <span className="nav-lang-dropdown__label">{navLangLabels[language] || currentLang?.short || "EN"}</span>
        <svg
          className={`nav-lang-dropdown__chevron ${langOpen ? "nav-lang-dropdown__chevron--open" : ""}`}
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {langOpen && (
        <div
          className="nav-lang-dropdown__menu"
          role="listbox"
          aria-label={t("selectLanguage")}
          onClick={(e) => e.stopPropagation()}
        >
          {availableLanguages.map((lang) => {
            const isActive = language === lang.code
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => selectLanguage(lang.code)}
                className={`nav-lang-dropdown__option ${isActive ? "nav-lang-dropdown__option--active" : ""}`}
              >
                <span>{lang.name}</span>
                {isActive && <span className="nav-lang-dropdown__check" aria-hidden>✓</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )

  return (
    <nav className="site-chrome site-chrome--header sticky top-0 z-[9999]" style={{ zIndex: 9999 }}>
      <div className="site-chrome-inner">
        <div className="flex h-14 items-center justify-between gap-3">
          <div className="flex flex-col items-start justify-center">
            <ScrollToTopLink to="/" className="site-brand">
              <img src="/logo.png" alt="Cosmic Learning logo" />
              <span className="leading-none hidden min-[420px]:inline">
                Cosmic&nbsp;
                <span className="text-accent">Learning</span>
              </span>
            </ScrollToTopLink>
            {billingEnabled && user && isPro && (
              <span className="ml-1.5 -mt-0.5 inline-flex items-center rounded-full bg-gradient-to-r from-primary to-accent px-1.5 py-[1px] text-[9px] font-bold uppercase leading-none tracking-[0.14em] text-primary-foreground shadow-sm">
                Pro
              </span>
            )}
          </div>

          <div className="hidden lg:flex flex-1 min-w-0 items-center justify-center">
            <div className="flex items-center justify-center gap-0.5 overflow-x-auto scrollbar-none">
              {navItems.map((item) => (
                <ScrollToTopLink key={item.name} to={item.path} className="site-nav-link">
                  {item.name}
                </ScrollToTopLink>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex flex-shrink-0 items-center gap-1 relative z-20">
            <button
              type="button"
              onClick={toggleTheme}
              className="site-icon-btn site-icon-btn--border"
              title={isDark ? t("lightMode") : t("darkMode")}
              aria-label={isDark ? t("lightMode") : t("darkMode")}
            >
              {isDark ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {user && (
              <ScrollToTopLink
                to="/settings"
                className="site-icon-btn site-icon-btn--border"
                title={t("settings")}
                aria-label={t("settings")}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </ScrollToTopLink>
            )}

            {languageToggle}

            {user ? (
              <div className="flex items-center gap-2 pl-1">
                <span className="text-muted-foreground text-xs hidden xl:inline max-w-[8rem] truncate">
                  {user.username || user.email}
                </span>
                <button
                  type="button"
                  onClick={logout}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-destructive-foreground bg-destructive hover:opacity-90 transition-opacity"
                >
                  {t("logout")}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 pl-1">
                <Link to="/login" viewTransition className="site-nav-link">
                  {t("login")}
                </Link>
                <Link
                  to="/signup"
                  viewTransition
                  className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 transition-opacity"
                >
                  {t("signup")}
                </Link>
              </div>
            )}
          </div>

          <div className="lg:hidden flex items-center gap-1 relative z-20">
            {languageToggle}
            <button
              type="button"
              onClick={toggleTheme}
              className="site-icon-btn"
              title={isDark ? t("lightMode") : t("darkMode")}
              aria-label={isDark ? t("lightMode") : t("darkMode")}
            >
              {isDark ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {user && (
              <ScrollToTopLink
                to="/settings"
                className="site-icon-btn"
                title={t("settings")}
                aria-label={t("settings")}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </ScrollToTopLink>
            )}

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="site-icon-btn site-icon-btn--border"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <svg className="h-4 w-4" stroke="currentColor" fill="none" viewBox="0 0 24 24" aria-hidden>
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="mobile-menu-fullscreen lg:hidden fixed inset-0 z-[99999] bg-background">
          <div className="site-chrome site-chrome--header flex h-14 items-center justify-between px-4">
            <div className="site-brand pointer-events-none">
              <img src="/logo.png" alt="" />
              <span className="text-sm font-semibold">
                Cosmic <span className="text-accent">Learning</span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(false)}
              className="site-icon-btn"
              aria-label="Close menu"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mobile-content h-full overflow-y-auto bg-background" style={{ minHeight: "calc(100vh - 56px)" }}>
            <div className="px-4 py-6 space-y-8">
              {navItems.length > 0 && (
                <div>
                  <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{t("navigation")}</h2>
                  <div className="space-y-1">
                    {navItems.map((item) => (
                      <ScrollToTopLink
                        key={item.name}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block w-full px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                      >
                        {item.name}
                      </ScrollToTopLink>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{t("language")}</h2>
                <div className="grid grid-cols-3 gap-2">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => {
                        selectLanguage(lang.code)
                        setIsMobileMenuOpen(false)
                      }}
                      className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                        language === lang.code
                          ? "bg-primary/10 text-primary border-primary/30"
                          : "bg-card text-foreground border-border hover:bg-muted"
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{t("profile")}</h2>
                {user ? (
                  <div className="space-y-2">
                    <p className="px-3 py-2 text-xs text-muted-foreground">
                      {t("welcome")}, <span className="text-foreground font-medium">{user.username || user.email}</span>
                    </p>
                    <ScrollToTopLink
                      to="/settings"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      {t("settings")}
                    </ScrollToTopLink>
                    <button
                      type="button"
                      onClick={() => {
                        logout()
                        setIsMobileMenuOpen(false)
                      }}
                      className="w-full px-3 py-2.5 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors text-left"
                    >
                      {t("logout")}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <ScrollToTopLink
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full px-3 py-2.5 rounded-lg text-sm font-medium text-center border border-border hover:bg-muted transition-colors"
                    >
                      {t("login")}
                    </ScrollToTopLink>
                    <ScrollToTopLink
                      to="/signup"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full px-3 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium text-center hover:opacity-90 transition-opacity"
                    >
                      {t("signup")}
                    </ScrollToTopLink>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
